import type { ReactElement } from "react";

import { useSelector } from "react-redux";

import type {
    ColumnPickerPluginConfig,
    SelectionModalProps,
} from "app/pages/sales-intelligence/common-components/column-picker";
import { ColumnsSelectionModal } from "app/pages/sales-intelligence/common-components/column-picker";
import { selectAppPlatformFilterValue } from "app/pages/sales-intelligence/modules/leads-search-filters/store/selectors";

import { useAppsColumnSelectionOptions } from "../../hooks/useAppsColumnSelectionOptions";
import { useAppsColumnSelectionMutation } from "../../query/mutations/useAppsColumnSelectionMutation";
import appsSearchI18nKeys from "../../utils/i18n";

type Props = SelectionModalProps & {
    pluginConfig?: ColumnPickerPluginConfig | null;
    onClose?: VoidFunction;
};

const AppsSelectionModalContainer = ({ isOpen, closeModal, pluginConfig, onClose }: Props): ReactElement => {
    const appPlatform = useSelector(selectAppPlatformFilterValue);

    const { mutateAsync, isPending } = useAppsColumnSelectionMutation(appPlatform);

    const { selectedColumnIds, categories, availableColumns } = useAppsColumnSelectionOptions(appPlatform);

    const handleCloseModal = (): void => {
        closeModal();
        onClose?.();
    };

    const handleApply = async (columns: readonly string[]): Promise<void> => {
        try {
            await mutateAsync({ columns });
            handleCloseModal();
        } catch {
            // error handled by mutation
        }
    };

    return (
        <ColumnsSelectionModal
            isOpened={isOpen}
            isApplyPending={isPending}
            title={{ key: appsSearchI18nKeys.columnPickerTitle(appPlatform) }}
            initialSelectedColumnIds={selectedColumnIds}
            columnOptions={availableColumns}
            categoryConfig={categories}
            pluginConfig={pluginConfig}
            onApply={handleApply}
            onCancel={handleCloseModal}
        />
    );
};

export default AppsSelectionModalContainer;
